import { useState, useEffect } from 'react';
import { Car, User, Mail, Phone, Calendar, DollarSign, Eye, Trash2, MessageSquare, CheckCircle, Clock, XCircle, ChevronDown, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { AdminLayout } from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700', icon: Clock },
  contacted: { label: 'Contacté', color: 'bg-yellow-100 text-yellow-700', icon: Phone },
  evaluated: { label: 'Évalué', color: 'bg-purple-100 text-purple-700', icon: Eye },
  offer_made: { label: 'Offre Faite', color: 'bg-orange-100 text-orange-700', icon: DollarSign },
  completed: { label: 'Complété', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  declined: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const CONDITION_LABELS = {
  excellent: 'Excellent',
  good: 'Bon',
  fair: 'Passable',
  poor: 'Mauvais'
};

const INTENT_LABELS = {
  sell: 'Vendre',
  'trade-in': 'Échange'
};

export default function AdminAppraisals() {
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  
  // Edit states
  const [editStatus, setEditStatus] = useState('');
  const [editOfferedPrice, setEditOfferedPrice] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAppraisals();
  }, [statusFilter]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchAppraisals = async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/appraisals`
        : `${API_URL}/api/appraisals?status=${statusFilter}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setAppraisals(data);
    } catch (error) {
      console.error('Error fetching appraisals:', error);
      toast.error('Erreur lors du chargement des évaluations');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (appraisal) => {
    setSelectedAppraisal(appraisal);
    setEditStatus(appraisal.status);
    setEditOfferedPrice(appraisal.offered_price?.toString() || '');
    setEditNotes(appraisal.admin_notes || '');
    setDetailOpen(true);
  };

  const saveChanges = async () => {
    if (!selectedAppraisal) return;
    setSaving(true);
    
    try {
      const params = new URLSearchParams();
      if (editStatus) params.append('status', editStatus);
      if (editOfferedPrice) params.append('offered_price', editOfferedPrice);
      if (editNotes !== undefined) params.append('admin_notes', editNotes);
      
      const response = await fetch(
        `${API_URL}/api/appraisals/${selectedAppraisal.id}?${params.toString()}`,
        { method: 'PUT', headers: getAuthHeaders() }
      );
      
      if (!response.ok) throw new Error('Failed to update');
      
      toast.success('Évaluation mise à jour');
      setDetailOpen(false);
      fetchAppraisals();
    } catch (error) {
      console.error('Error updating appraisal:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const deleteAppraisal = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette évaluation?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/appraisals/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Évaluation supprimée');
      setDetailOpen(false);
      fetchAppraisals();
    } catch (error) {
      console.error('Error deleting appraisal:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredAppraisals = appraisals.filter(a => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      a.name.toLowerCase().includes(searchLower) ||
      a.email.toLowerCase().includes(searchLower) ||
      `${a.year} ${a.make} ${a.model}`.toLowerCase().includes(searchLower) ||
      a.vin?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stats
  const stats = {
    total: appraisals.length,
    new: appraisals.filter(a => a.status === 'new').length,
    inProgress: appraisals.filter(a => ['contacted', 'evaluated', 'offer_made'].includes(a.status)).length,
    completed: appraisals.filter(a => a.status === 'completed').length
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Évaluations de Véhicules</h1>
            <p className="text-slate-500">Demandes de vente/échange de clients</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
          </div>
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
            <p className="text-sm text-blue-600">Nouveaux</p>
            <p className="text-2xl font-bold text-blue-700">{stats.new}</p>
          </div>
          <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
            <p className="text-sm text-yellow-600">En cours</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.inProgress}</p>
          </div>
          <div className="bg-green-50 rounded-xl border border-green-200 p-4">
            <p className="text-sm text-green-600">Complétés</p>
            <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher par nom, email, véhicule..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Appraisals List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Chargement...</div>
          ) : filteredAppraisals.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Aucune évaluation trouvée
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredAppraisals.map((appraisal) => {
                const statusConfig = STATUS_CONFIG[appraisal.status] || STATUS_CONFIG.new;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div 
                    key={appraisal.id}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => openDetail(appraisal)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-slate-800">
                            {appraisal.year} {appraisal.make} {appraisal.model} {appraisal.trim}
                          </h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {statusConfig.label}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="h-3.5 w-3.5" />
                            {appraisal.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" />
                            {appraisal.email}
                          </span>
                          {appraisal.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" />
                              {appraisal.phone}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(appraisal.created_at)}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                          <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded">
                            {appraisal.mileage?.toLocaleString()} km
                          </span>
                          <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded">
                            {CONDITION_LABELS[appraisal.condition] || appraisal.condition}
                          </span>
                          <span className={`px-2 py-1 rounded ${appraisal.intent === 'trade-in' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                            {INTENT_LABELS[appraisal.intent] || appraisal.intent}
                          </span>
                          {appraisal.desired_price && (
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              Souhaité: {appraisal.desired_price.toLocaleString()} $
                            </span>
                          )}
                          {appraisal.offered_price && (
                            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded font-medium">
                              Offre: {appraisal.offered_price.toLocaleString()} $
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Détails de l'Évaluation</DialogTitle>
            </DialogHeader>
            
            {selectedAppraisal && (
              <div className="space-y-6">
                {/* Vehicle Info */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Car className="h-4 w-4" /> Véhicule
                  </h3>
                  <p className="text-xl font-bold text-blue-600 mb-2">
                    {selectedAppraisal.year} {selectedAppraisal.make} {selectedAppraisal.model} {selectedAppraisal.trim}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-slate-500">Kilométrage:</span> {selectedAppraisal.mileage?.toLocaleString()} km</p>
                    {selectedAppraisal.vin && <p><span className="text-slate-500">VIN:</span> {selectedAppraisal.vin}</p>}
                    <p><span className="text-slate-500">Transmission:</span> {selectedAppraisal.transmission}</p>
                    <p><span className="text-slate-500">Traction:</span> {selectedAppraisal.drivetrain}</p>
                    {selectedAppraisal.exterior_color && <p><span className="text-slate-500">Couleur ext.:</span> {selectedAppraisal.exterior_color}</p>}
                    {selectedAppraisal.fuel_type && <p><span className="text-slate-500">Carburant:</span> {selectedAppraisal.fuel_type}</p>}
                  </div>
                </div>

                {/* Condition */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-800 mb-3">État du Véhicule</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500">Condition:</span> <strong>{CONDITION_LABELS[selectedAppraisal.condition]}</strong></p>
                    <p><span className="text-slate-500">Historique accidents:</span> {selectedAppraisal.accident_history === 'none' ? 'Aucun' : selectedAppraisal.accident_history === 'minor' ? 'Mineur' : 'Majeur'}</p>
                    {selectedAppraisal.mechanical_issues && (
                      <p><span className="text-slate-500">Problèmes mécaniques:</span> {selectedAppraisal.mechanical_issues}</p>
                    )}
                  </div>
                </div>

                {/* Contact */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" /> Contact
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-slate-500">Nom:</span> {selectedAppraisal.name}</p>
                    <p><span className="text-slate-500">Email:</span> <a href={`mailto:${selectedAppraisal.email}`} className="text-blue-600 hover:underline">{selectedAppraisal.email}</a></p>
                    {selectedAppraisal.phone && <p><span className="text-slate-500">Téléphone:</span> <a href={`tel:${selectedAppraisal.phone}`} className="text-blue-600 hover:underline">{selectedAppraisal.phone}</a></p>}
                  </div>
                </div>

                {/* Intent */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" /> Intention
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-slate-500">Type:</span> <strong>{INTENT_LABELS[selectedAppraisal.intent]}</strong></p>
                    {selectedAppraisal.desired_price && <p><span className="text-slate-500">Prix souhaité:</span> <strong>{selectedAppraisal.desired_price.toLocaleString()} $</strong></p>}
                    <p><span className="text-slate-500">Délai:</span> {selectedAppraisal.timeline}</p>
                  </div>
                </div>

                {selectedAppraisal.additional_notes && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h3 className="font-semibold text-slate-800 mb-2">Notes du Client</h3>
                    <p className="text-sm text-slate-600">{selectedAppraisal.additional_notes}</p>
                  </div>
                )}

                {/* Admin Actions */}
                <div className="border-t pt-6 space-y-4">
                  <h3 className="font-semibold text-slate-800">Actions Admin</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-600 mb-1 block">Statut</label>
                      <Select value={editStatus} onValueChange={setEditStatus}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm text-slate-600 mb-1 block">Prix Offert ($)</label>
                      <Input
                        type="number"
                        placeholder="Ex: 12000"
                        value={editOfferedPrice}
                        onChange={(e) => setEditOfferedPrice(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Notes Admin</label>
                    <Textarea
                      placeholder="Notes internes..."
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex justify-between">
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => deleteAppraisal(selectedAppraisal.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                    
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setDetailOpen(false)}>
                        Annuler
                      </Button>
                      <Button onClick={saveChanges} disabled={saving}>
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
